var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from) {
    for (var i = 0, il = from.length, j = to.length; i < il; i++, j++)
        to[j] = from[i];
    return to;
};
import { jsx as _jsx } from "react/jsx-runtime";
import { useCallback, useMemo, useLayoutEffect, useContext, useState } from 'react';
import { isObject } from './is';
import useForceUpdate from '../hooks/useForceUpdate';
import usePrevious from '../hooks/usePrevious';
import createNamedContext from './createNamedContext';
var noop = function () { return undefined; };
export var createDescendantContext = function (name) {
    return createNamedContext(name, {
        descendants: [],
        register: noop,
        unregister: noop
    });
};
export var useDescendantInit = function (initialValue) {
    if (initialValue === void 0) { initialValue = []; }
    return useState(initialValue);
};
export var useDescendants = function (Ctx) { return useContext(Ctx).descendants; };
/**
 * Descendant Provider
 *
 * This context is used to manage a registry of options rendered via the ComboboxOption
 * component. Special care is taken to ensure DOM / option order remains in sync
 * so remote navigation of internal members stays consistent and predictable.
 */
export var DescendantProvider = function (_a) {
    var Ctx = _a.context, descendants = _a.descendants, setDescendants = _a.setDescendants, children = _a.children;
    var register = useCallback(function (_a) {
        var element = _a.element, _ = _a._, rest = __rest(_a, ["element", "_"]);
        if (!element)
            return;
        setDescendants(function (descendantsState) {
            var nextDescendantsState;
            if (descendantsState.length === 0) {
                nextDescendantsState = [
                    __assign(__assign({}, rest), { element: element, index: 0 })
                ];
            }
            else if (descendantsState.find(function (o) { return o.element === element; })) {
                nextDescendantsState = descendantsState;
            }
            else {
                var DOMIndex = descendantsState.findIndex(function (o) {
                    if (!o.element || !element)
                        return false;
                    // Returns a bitmask...
                    // https://developer.mozilla.org/en-US/docs/Web/API/Node/compareDocumentPosition
                    return Boolean(o.element.compareDocumentPosition(element) &
                        Node.DOCUMENT_POSITION_PRECEDING);
                });
                var nextDescendant = __assign(__assign({}, rest), { element: element, index: DOMIndex });
                if (DOMIndex === -1) {
                    nextDescendantsState = __spreadArray(__spreadArray([], __read(descendantsState)), [nextDescendant]);
                }
                else {
                    nextDescendantsState = __spreadArray(__spreadArray(__spreadArray([], __read(descendantsState.slice(0, DOMIndex))), [
                        nextDescendant
                    ]), __read(descendantsState.slice(DOMIndex)));
                }
            }
            return nextDescendantsState.map(function (d, nextIndex) { return (__assign(__assign({}, d), { index: nextIndex })); });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var unregister = useCallback(function (element) {
        if (!element)
            return;
        setDescendants(function (descendantsState) {
            return descendantsState.filter(function (_a) {
                var e = _a.element;
                return e !== element;
            });
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    var value = useMemo(function () { return ({
        descendants: descendants,
        register: register,
        unregister: unregister
    }); }, [descendants, register, unregister]);
    return _jsx(Ctx.Provider, __assign({ value: value }, { children: children }), void 0);
};
export var useDescendant = function (descendant, context) {
    var _a = useContext(context), register = _a.register, unregister = _a.unregister, descendants = _a.descendants;
    var forceUpdate = useForceUpdate();
    var index = descendants.findIndex(function (d) { return d.element === descendant.element; });
    var previousDescendants = usePrevious(descendants);
    var someDescendantsHaveChanged = descendants.some(function (_, idx) { var _a; return descendant.element !== ((_a = previousDescendants === null || previousDescendants === void 0 ? void 0 : previousDescendants[idx]) === null || _a === void 0 ? void 0 : _a.element); });
    var previousDescendant = usePrevious(descendant);
    var descendantDataDidChange = isObject(descendant.data) &&
        Object.keys(descendant.data).some(function (key) { return (previousDescendant === null || previousDescendant === void 0 ? void 0 : previousDescendant.data[key]) !== descendant.data[key]; });
    useLayoutEffect(function () {
        if (descendant.disabled)
            return;
        if (!descendant.element) {
            forceUpdate();
            return;
        }
        register(__assign(__assign({}, descendant), { index: index }));
        return function () { return unregister(descendant === null || descendant === void 0 ? void 0 : descendant.element); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        forceUpdate,
        index,
        register,
        unregister,
        someDescendantsHaveChanged,
        descendant.value,
        descendant.element,
        descendant.index,
        descendant.disabled,
        descendantDataDidChange
    ]);
    return index;
};
